import React, { useEffect, useState } from 'react'
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons'

import PixelTabIcons from 'app/common/PixelTabIcons'
import ArrowRightFromBracketIcon from 'client/utils/icons/arrow-right-from-bracket-icon'
import { OrdersContext } from 'contexts/OrdersContext'
import {
  $DCDangerColor,
  $DCDangerLightColor,
  $DCInfoColor,
  $DCInfoLightColor,
  $DCprimaryColor,
  $DCprimaryLightColor,
  $DCSuccessColor,
  $DCSuccessLightColor,
  DcButton,
  DcDropDown,
  DcInput,
  DcTag,
  PixelDate,
  PixelDiv,
  PixelFactoryContext,
  PixelFlexBox,
  PixelIcon,
  PixelPageContainer,
  PixelPopover,
  PixelTable,
  PixelTag,
  PixelText,
  Section,
  SectionContent,
} from 'pixel-base'
import { env } from 'shared/env'
import styled from 'styled-components'
import { get } from 'utils/api-routes.util'
import { IPixelFactory } from '../../../../../../../decl.d'

export type IInventoryUploads = {
  total_rows: number
  updating_count: number
  updated_count: number
  invalid_count: number
  no_changes_count: number
  created_at: Date
  warehouse_name: string
  file_name: string
  request_status: string
  name: string
}
interface IDetails {
  bulk_request_detail_id: string
  product_sku: string
  stock_count: string
  outward_update_meta: IOutwardUpdateMeta[]
  validation_status: string
}

interface IOutwardUpdateMeta {
  store_id: number
  type: string
  status: string
}

const UploadHistoryDetails = (props) => {
  const { requestId } = props
  const baseURL = env.SERVER_ADDR
  const { token } = JSON.parse(
    localStorage.getItem('user_info') || '{token: null}',
  )
  const { state } = React.useContext(OrdersContext)
  const { showLoader, hideLoader } =
    React.useContext<IPixelFactory>(PixelFactoryContext)
  const [isClosePopover, setIsClosePopover] = useState(false)
  const [inventoryUploads, setInventoryUploads] = useState<IInventoryUploads>({
    total_rows: 0,
    updating_count: 0,
    invalid_count: 0,
    no_changes_count: 0,
    updated_count: 0,
    created_at: new Date(),
    warehouse_name: '',
    file_name: '',
    request_status: '',
    name: '',
  })
  const [filters, setFilters] = useState<{
    search: string | null
    validation_status:
      | 'update-inprogress'
      | 'success'
      | 'invalid'
      | 'no-changes'
      | 'all'
    pageno: number
    pagesize: number
  }>({
    search: null,
    validation_status: 'all',
    pageno: 0,
    pagesize: 50,
  })
  const [totalRows, setTotalRows] = useState(0)
  const columns = [
    {
      dataKey: 'bulk_request_detail_id',
      name: 'ID',
      sortable: true,
      selector: (row: IDetails) => row.bulk_request_detail_id,
    },
    {
      dataKey: 'product_sku',
      name: <PixelText>Product SKU</PixelText>,
      sortable: true,
      selector: (row: IDetails) => row.product_sku,
    },
    {
      dataKey: 'stock_count',
      name: <PixelText>Stock</PixelText>,
      sortable: true,
      selector: (row: IDetails) => row.stock_count,
    },
    {
      dataKey: 'update_type',
      name: 'Update Type',
      sortable: true,
      selector: (row) =>
        row.update_type !== null ? (
          <DcTag
            textColor={
              row.update_type == 'add' ? $DCSuccessColor : $DCDangerColor
            }
            customBackgroundColor={
              row.update_type == 'add'
                ? $DCSuccessLightColor
                : $DCDangerLightColor
            }
          >
            {row.update_type}
          </DcTag>
        ) : (
          '--'
        ),
    },
    {
      dataKey: 'validation_status',
      name: <PixelText>Validation Status</PixelText>,
      selector: (row: IDetails) => (
        <DcTag
          textColor={
            row.validation_status == 'invalid'
              ? $DCDangerColor
              : row.validation_status === 'updating'
              ? $DCSuccessColor
              : row.validation_status === 'no-changes'
              ? $DCprimaryColor
              : row.validation_status === 'update-inprogress' && $DCInfoColor
          }
          customBackgroundColor={
            row.validation_status == 'invalid'
              ? $DCDangerLightColor
              : row.validation_status === 'updating'
              ? $DCSuccessLightColor
              : row.validation_status === 'no-changes'
              ? $DCprimaryLightColor
              : row.validation_status === 'update-inprogress' &&
                $DCInfoLightColor
          }
        >
          {row.validation_status}
        </DcTag>
      ),
    },
    {
      dataKey: 'outward_update_meta',
      name: <PixelText>Outward Updates</PixelText>,
      selector: (row: IDetails) =>
        row.outward_update_meta?.map((item: IOutwardUpdateMeta, index) => {
          return (
            <PixelFlexBox key={item} justifyContent="column">
              <StyledPixelTabsIcon key={index}>
                <PixelTabIcons
                  store_type={
                    state?.linkedStores.find(
                      (store) => store.store_id === item.store_id,
                    )?.store_type
                  }
                />{' '}
                <PixelTag
                  customBackgroundColor={item.status == 'failed' && '#f44336'}
                >
                  {item.status}
                </PixelTag>
              </StyledPixelTabsIcon>
            </PixelFlexBox>
          )
        }),
    },
  ]
  const [inventoryUploadsData, setInventoryUploadsData] = useState([])
  const ref = React.useRef(null)
  const getData = () => {
    showLoader(ref)
    const apiFilters = {
      search: filters.search ? filters.search : 'null',
      validation_status: filters.validation_status,
      pageno: filters.pageno,
      pagesize: filters.pagesize,
    }

    get(`/products/upload/${requestId}/details`, apiFilters).subscribe(
      (res: any) => {
        hideLoader(ref)
        setInventoryUploads(res.response.data.header)
        setTotalRows(res.response?.data?.total_count?.total_rows || 0)
        setInventoryUploadsData(res.response?.data?.details || [])
      },
    )
  }

  useEffect(() => {
    getData()
  }, [filters])

  const detailsArray = [
    {
      label: 'Request Status:',
      value:
        (
          <DcTag
            textColor={$DCInfoColor}
            customBackgroundColor={$DCInfoLightColor}
          >
            {inventoryUploads?.request_status}
          </DcTag>
        ) || '--',
    },
    { label: 'File Name:', value: inventoryUploads?.file_name },
    { label: 'Warehouse:', value: inventoryUploads?.warehouse_name },
    { label: 'Uploaded By:', value: inventoryUploads?.name },
    {
      label: 'Created At:',
      value: (
        <PixelDate
          color={'#8e959b'}
          value={`${inventoryUploads?.created_at} UTC`}
          format={'dateWithTime'}
        />
      ),
    },
    { label: 'Total Products Uploaded:', value: inventoryUploads?.total_rows },
    {
      label: 'updated Products:',
      value: inventoryUploads?.updated_count,
      isShowPopOver: true,
      popoverContent: 'Number of products that are updated.',
    },
    {
      label: 'Update inprogress:',
      value: inventoryUploads?.updating_count,
      isShowPopOver: true,
      popoverContent: 'Number of products that are being updated.',
    },
    {
      label: 'Invalid Products:',
      value: inventoryUploads?.invalid_count,
      isShowPopOver: true,
      popoverContent:
        'Number of products that are not in the system.Most likely the product SKU is incorrect or deleted product.',
    },
    {
      label: 'No Changes Detect:',
      value: inventoryUploads?.no_changes_count,
      isShowPopOver: true,
      popoverContent:
        'Number of products that are not updated because there are no changes in the product details.',
    },
  ]

  const tableDetailStyle = {
    width: '100%',
    display: 'table',
    tableLayout: 'fixed',
    borderSpacing: 0,
    borderCollapse: 'collapse',
  }

  const identifierStyle = {
    fontSize: '16px',
    fontWeight: '600',
    color: '#8e959b',
    display: 'flex',
    // padding: '10px',
    gap: '10px',
    width: '100%',
    // backgroundColor: '#e9ecef',
  }

  const detailStyle = {
    width: '60%',
    // backgroundColor: '#e9ecef78',
    fontSize: '16px',
    fontWeight: '400',
    color: '#8e959b',
    display: 'table-cell',
    verticalAlign: 'middle',
    paddingLeft: '10px',
  }
  const handlePageChange = (page) => {
    if (filters.pageno === page - 1 || page < 0) return

    setFilters({
      ...filters,
      pageno: page - 1,
    })
  }
  const handlePerRowsChange = async (newPerPage) => {
    setFilters({
      ...filters,
      pagesize: newPerPage,
    })
  }
  return (
    <>
      <PixelPageContainer>
        <PixelFlexBox
          flexDirection="row"
          justifyContent="flex-end"
          height="auto"
          style={{ padding: '0px 20px 10px 0px' }}
        >
          <DcButton
            onClick={() => {
              window.open(
                `${baseURL}api/v2/export/upload/inventory/file/${token}/${requestId}`,
              )
            }}
            size="sm"
            withIcon={true}
          >
            <ArrowRightFromBracketIcon size={16} />
            <span>Export File</span>
          </DcButton>
        </PixelFlexBox>
        <Section ref={ref} className="upload-history">
          <SectionContent>
            {detailsArray.map(
              (
                detail: {
                  isShowPopOver?: boolean
                  label: string
                  value: string | JSX.Element | number
                  popoverContent?: string
                },
                index,
              ) => (
                <PixelDiv key={index} style={tableDetailStyle}>
                  <PixelDiv style={identifierStyle}>
                    {detail.label}
                    {detail.isShowPopOver && (
                      <PixelPopover
                        PopoverContent={detail.popoverContent}
                        popoverPlacement={'right'}
                        closePopover={isClosePopover}
                        overlayButton={
                          <PixelDiv height={'20px'} width={'20px'}>
                            <PixelIcon
                              color={$DCprimaryColor}
                              icon={faInfoCircle}
                            />
                          </PixelDiv>
                        }
                        styledPopover={{
                          width: '355px',
                          maxWidth: '355px',
                          padding: '10px',
                          position: 'relative !important',
                        }}
                        onPopoverExit={() => {
                          setIsClosePopover(false)
                          return {}
                        }}
                      />
                    )}
                  </PixelDiv>

                  <PixelDiv style={detailStyle}>{detail.value}</PixelDiv>
                </PixelDiv>
              ),
            )}
          </SectionContent>
        </Section>
        <PixelFlexBox
          flexDirection="row"
          justifyContent="space-between"
          height="auto"
          style={{ padding: '20px 20px 10px 20px' }}
        >
          <PixelText textSize="20px" style={{ fontWeight: '600' }}>
            Details
          </PixelText>
          <PixelFlexBox width="60%" gap="10px" justifyContent="flex-end">
            <DcInput
              showsearchicon={1}
              placeholder="Search"
              name="search"
              onChange={(e) =>
                setFilters({ ...filters, search: e.target.value })
              }
              value={filters.search || ''}
            />
            <DcDropDown
              style={{ backgroundColor: 'white' }}
              placeholder="Select Status"
              options={[
                {
                  status: 'All Status',
                  id: 'all',
                },
                {
                  status: 'Update Inprogress',
                  id: 'update-inprogress',
                },
                {
                  status: 'Success',
                  id: 'success',
                },
                {
                  status: 'Invalid',
                  id: 'invalid',
                },
                {
                  status: 'No Changes',
                  id: 'no-changes',
                },
              ].map((item: { status: string; id: string | null }) => {
                return {
                  label: item.status,
                  value: item.id,
                }
              })}
              value={filters.validation_status}
              onChange={(e) => {
                setFilters({
                  ...filters,
                  validation_status: e.target.value,
                })
              }}
            />
          </PixelFlexBox>
        </PixelFlexBox>
        <Section ref={ref} className="upload-history">
          <SectionContent>
            <PixelTable
              columns={columns}
              data={inventoryUploadsData}
              paginationServer
              paginationPerPage={filters.pagesize}
              paginationRowsPerPageOptions={[50, 100, 250, 500]}
              onChangeRowsPerPage={handlePerRowsChange}
              paginationDefaultPage={Number(filters.pageno) + 1}
              onChangePage={handlePageChange}
              paginationTotalRows={totalRows}
              fixedHeader
              fixedHeaderScrollHeight="calc(100vh - 460px)"
            />
          </SectionContent>
        </Section>
      </PixelPageContainer>
    </>
  )
}

const StyledPixelTabsIcon = styled(PixelFlexBox)`
  > div {
    > img {
      width: 18px;
      height: 18px;
    }
  }
`
export default UploadHistoryDetails
